import React, { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import { MonthlyQuotes, usageService } from '../../../services/UsageService';
import { BAR_MUTED, BORDER, DEEP, memberName, monthLabel, num } from './memberUsageUtils';

const MONTHS = 6;

/**
 * Quotes generated each month — for all advisors or one advisor — over the
 * last six billing months ending at the month selected on the page.
 * Disabled users are left out unless the admin asks for them.
 */
const QuotesByMonthSection: React.FC<{ endMonth?: string }> = ({ endMonth }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [advisor, setAdvisor] = useState<string>('all');
  const [data, setData] = useState<MonthlyQuotes | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!endMonth) return;
    let cancelled = false;
    setError(false);
    usageService
      .getMonthlyQuotes(endMonth, MONTHS, includeDisabled)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [endMonth, includeDisabled]);

  // The picked advisor may disappear when disabled users are hidden again.
  const selected = data?.members.find((m) => m.key === advisor) ?? null;
  useEffect(() => {
    if (advisor !== 'all' && data && !selected) setAdvisor('all');
  }, [advisor, data, selected]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.months.map((m, i) => ({
      month: monthLabel(m, locale, true),
      all: data.totals[i],
      advisor: selected ? selected.quotes[i] : data.totals[i],
    }));
  }, [data, selected, locale]);

  if (!endMonth) return null;
  const last = data ? data.months.length - 1 : 0;
  const monthTotal = data ? data.totals[last] : 0;
  const ranking = data ? [...data.members].filter((m) => m.quotes[last] > 0).sort((a, b) => b.quotes[last] - a.quotes[last]) : [];
  const allLabel = t('usage.members.allAdvisors', { defaultValue: 'All advisors' });
  const name = (m: { key: string; name: string }) => memberName(m.key, m.name, t);

  return (
    <section aria-labelledby="quotes-by-month-h" className="relative mt-10 bg-white rounded-2xl overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
      <div className="flex flex-wrap items-end justify-between gap-4 px-6 py-5" style={{ borderBottom: `1px solid ${BORDER}` }}>
        <div>
          <h2 id="quotes-by-month-h" className="text-lg font-semibold text-gray-900">
            {t('usage.members.quotesPerMonth', { defaultValue: 'Quotes per month' })}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {t('usage.members.quotesPerMonthHint', { defaultValue: 'Total quotes generated each month, for all advisors or one advisor.' })}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">
            {t('usage.members.advisor', { defaultValue: 'Advisor' })}
            <select
              value={selected ? selected.key : 'all'}
              onChange={(e) => setAdvisor(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-900 min-w-[220px] min-h-[40px]"
            >
              <option value="all">{allLabel}</option>
              {(data?.members || []).map((m) => (
                <option key={m.key} value={m.key}>
                  {name(m)}{m.disabled ? ` (${t('usage.members.disabled', { defaultValue: 'disabled' })})` : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 min-h-[40px] cursor-pointer">
            <input
              type="checkbox"
              checked={includeDisabled}
              onChange={(e) => setIncludeDisabled(e.target.checked)}
              className="w-4 h-4"
              style={{ accentColor: DEEP }}
            />
            {t('usage.members.includeDisabled', { defaultValue: 'Include disabled users' })}
          </label>
        </div>
      </div>

      {error && (
        <div className="px-6 py-4 text-sm text-red-800 bg-red-50">
          {t('usage.members.loadError', { defaultValue: 'Could not load advisor usage.' })}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3">
            <div className="lg:col-span-2 px-6 py-5 lg:border-r" style={{ borderColor: BORDER }}>
              <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500">
                    {selected ? name(selected) : allLabel} · {monthLabel(data.months[last], locale)}
                  </div>
                  <div className="text-3xl font-semibold text-gray-900 mt-1">
                    {num(selected ? selected.quotes[last] : monthTotal)}{' '}
                    <span className="text-sm font-medium text-gray-500">
                      {selected
                        ? t('usage.members.shareOf', {
                            defaultValue: '{{pct}}% of {{total}} quotes',
                            pct: monthTotal ? Math.round((selected.quotes[last] / monthTotal) * 100) : 0,
                            total: num(monthTotal),
                          })
                        : t('usage.members.quotesInMonths', {
                            defaultValue: 'quotes · {{total}} in {{count}} months',
                            total: num(data.grand_total),
                            count: data.months.length,
                          })}
                    </span>
                  </div>
                </div>
              </div>
              <div className="h-56 mt-4" role="img" aria-label={t('usage.members.chartAria', {
                defaultValue: 'Quotes per month for {{who}}', who: selected ? name(selected) : allLabel,
              })}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }} barGap={4}>
                    <CartesianGrid vertical={false} stroke="#f1eee8" />
                    <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#6b665c' }} />
                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#6b665c' }} />
                    <Tooltip cursor={{ fill: '#faf9f6' }} />
                    {selected && (
                      <Bar dataKey="all" name={allLabel} fill={BAR_MUTED} radius={[6, 6, 0, 0]} maxBarSize={36} />
                    )}
                    <Bar dataKey="advisor" name={selected ? name(selected) : allLabel} fill={DEEP} radius={[6, 6, 0, 0]} maxBarSize={36} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="px-6 py-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {t('usage.members.ranking', { defaultValue: '{{month}} ranking', month: monthLabel(data.months[last], locale, true) })}
              </h3>
              {ranking.length === 0 ? (
                <p className="text-sm text-gray-500">{t('usage.members.noQuotes', { defaultValue: 'No quotes this month yet.' })}</p>
              ) : (
                <ol className="flex flex-col gap-1">
                  {ranking.map((m, i) => {
                    const on = selected?.key === m.key;
                    return (
                      <li key={m.key}>
                        <button
                          type="button"
                          aria-pressed={on}
                          onClick={() => setAdvisor(on ? 'all' : m.key)}
                          className="w-full flex items-center gap-3 text-sm text-gray-900 px-2.5 py-2 min-h-[40px] rounded-lg border text-left"
                          style={{ borderColor: on ? '#c9c2f3' : 'transparent', background: on ? '#f3f1fd' : 'transparent' }}
                        >
                          <span className="w-5 text-gray-500 tabular-nums">{i + 1}</span>
                          <span className="flex-1 truncate">{name(m)}</span>
                          <span className="font-semibold tabular-nums">{num(m.quotes[last])}</span>
                        </button>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </div>

          <div className="overflow-x-auto" style={{ borderTop: `1px solid ${BORDER}` }}>
            <table className="w-full min-w-[760px] text-sm tabular-nums">
              <caption className="sr-only">{t('usage.members.matrixCaption', { defaultValue: 'Quotes per advisor per month' })}</caption>
              <thead>
                <tr className="bg-[#faf9f6] text-xs uppercase tracking-wider text-gray-500">
                  <th scope="col" className="text-left font-medium px-6 py-3">{t('usage.members.advisor', { defaultValue: 'Advisor' })}</th>
                  {data.months.map((m) => (
                    <th key={m} scope="col" className="text-right font-medium px-3 py-3">{monthLabel(m, locale, true)}</th>
                  ))}
                  <th scope="col" className="text-right font-medium pl-3 pr-6 py-3">
                    {t('usage.members.nMonths', { defaultValue: '{{count}} months', count: data.months.length })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.members.map((m) => (
                  <tr
                    key={m.key}
                    className="border-b"
                    style={{ borderColor: '#f1eee8', background: selected?.key === m.key ? '#f3f1fd' : undefined, color: m.disabled ? '#6b665c' : undefined }}
                  >
                    <th scope="row" className="text-left font-medium px-6 py-2.5">
                      <button type="button" onClick={() => setAdvisor(selected?.key === m.key ? 'all' : m.key)} className="hover:underline text-left">
                        {name(m)}
                      </button>
                      {m.disabled && (
                        <span className="ml-2 text-[11px] font-semibold text-gray-600 bg-[#f1efea] px-2 py-0.5 rounded-full">
                          {t('usage.members.disabledBadge', { defaultValue: 'Disabled' })}
                        </span>
                      )}
                    </th>
                    {m.quotes.map((q, i) => (
                      <td key={i} className="text-right px-3 py-2.5">{num(q)}</td>
                    ))}
                    <td className="text-right font-semibold pl-3 pr-6 py-2.5">{num(m.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[#faf9f6] font-semibold">
                  <th scope="row" className="text-left px-6 py-3">{allLabel}</th>
                  {data.totals.map((q, i) => (
                    <td key={i} className="text-right px-3 py-3">{num(q)}</td>
                  ))}
                  <td className="text-right pl-3 pr-6 py-3">{num(data.grand_total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </section>
  );
};

export default QuotesByMonthSection;
